// This hook is intended to be equivalent to useState, but it stores the state
// in the query params in the URL. Inspiration taken from:
// https://pierrehedkvist.com/posts/react-state-url

import { useHistory } from 'react-router-dom';

type QueryParam = object | string | number | boolean | null | undefined;

type UseQueryParamOptions = {
  serialize: ((deserialized: QueryParam) => string) | undefined,
  deserialize: ((serialized: string) => QueryParam) | undefined,
} | undefined;

export default function useQueryParam(
  paramName: string,
  defaultValue: QueryParam,
  options: UseQueryParamOptions,
): [QueryParam, (newValue: QueryParam) => void] {
  const history = useHistory();
  const searchParams = new URLSearchParams(history.location.search);
  const existingValue = searchParams.get(paramName);

  let value: QueryParam = defaultValue;

  if (existingValue !== null) {
    if (options?.deserialize) {
      value = options.deserialize(existingValue);
    } else {
      try {
        value = JSON.parse(existingValue);
      } catch {
        value = existingValue;
      }
    }
  }

  const updateURL = (newValue: QueryParam): void => {
    const newSearchParams = new URLSearchParams(history.location.search);

    if (newValue === null || newValue === undefined) {
      newSearchParams.delete(paramName);
    } else {
      let serialized: string;

      if (options?.serialize) {
        serialized = options.serialize(newValue);
      } else if (typeof newValue === 'object') {
        serialized = JSON.stringify(newValue);
      } else {
        serialized = newValue.toString();
      }

      newSearchParams.set(paramName, serialized);
    }

    history.replace({ search: newSearchParams.toString() });
  };

  const setValue = (newValue: QueryParam | ((oldValue: QueryParam) => QueryParam)): void => {
    if (typeof newValue === 'function') {
      updateURL(newValue(value));
    } else {
      updateURL(newValue);
    }
  };

  return [
    value,
    setValue,
  ];
}
